import { Controller } from "@hotwired/stimulus"
import { get } from "@rails/request.js"

/* global initFlowbite */

// Connects to data-controller="notification"
export default class extends Controller {
  static targets = ["item", "items", "badge", "loader"]
  static values = { userId: Number }

  connect() {
    this.updateBadgeOnLoad()
  }

  async open() {
    this.openDrawer()

    if (this.itemsTarget.innerHTML.trim() !== "") return

    await this.loadNotifications()
  }

  async loadNotifications() {
    if (!this.hasItemsTarget) {
      console.error("itemsTarget is missing. HTML structure is broken.")
      return
    }

    const response = await get("/homes/notifications", {
      responseKind: "html"
    })

    if (response.ok) {
      this.itemsTarget.innerHTML = await response.text
      this.itemsTarget.classList.remove("hidden")

      if (this.hasLoaderTarget) {
        this.loaderTarget.classList.add("hidden")
      }

      this.initializeFlowbite()
      this.updateBadge()
    }
  }

  initializeFlowbite() {
    if (typeof initFlowbite !== 'undefined') {
      initFlowbite()
      this.setupModalImageLazyLoading()
      this.setupTabLazyLoading()
    } else {
      console.error('Flowbite initFlowbite function not found. Make sure flowbite.turbo.js is loaded.')
    }
  }

  setupModalImageLazyLoading() {
    if (!this.hasItemsTarget) return

    const modalButtons = this.itemsTarget.querySelectorAll('[data-modal-toggle]')

    modalButtons.forEach((button) => {
      const modalId = button.dataset.modalToggle
      if (!modalId) return

      const modal = this.itemsTarget.querySelector(`#${modalId}`)
      if (!modal) return

      const imageData = modal.dataset.notificationImage
      const imageContainer = modal.querySelector('.modal-image-container')

      if (!imageData || !imageContainer) return

      button.addEventListener('click', (_e) => {
        if (imageContainer.innerHTML.trim() === '' || imageContainer.innerHTML.includes('Image will be loaded')) {
          const img = document.createElement('img')
          img.src = `data:image/jpeg;base64,${imageData}`
          img.className = 'w-full rounded-lg md:h-[700px] sm:h-[350px] h-[250px]'
          img.alt = 'Notification Image'
          imageContainer.innerHTML = ''
          imageContainer.appendChild(img)
        }
      }, { once: false })
    })
  }

  setupTabLazyLoading() {
    const tabButtons = document.querySelectorAll('#notification-tabs button[data-tab-name]')

    tabButtons.forEach((button) => {
      button.addEventListener('click', async (_e) => {
        const tabName = button.dataset.tabName
        const tabPanel = document.querySelector(button.dataset.tabsTarget)

        if (!tabPanel || tabPanel.dataset.loaded === 'true' || tabName === 'all') {
          return
        }

        try {
          const response = await get(`/homes/notifications?tab=${tabName}`, {
            responseKind: "html"
          })

          if (response.ok) {
            tabPanel.innerHTML = await response.text
            tabPanel.dataset.loaded = 'true'

            if (typeof initFlowbite !== 'undefined') {
              initFlowbite()
            }

            this.setupModalImageLazyLoading()
          }
        } catch (error) {
          console.error(`Error loading ${tabName} tab:`, error)
          tabPanel.innerHTML = '<div class="p-6 text-center text-red-500">Error loading notifications</div>'
        }
      })
    })
  }

  async loadMore(event) {
    const button = event.currentTarget
    const page = button.dataset.notificationPage

    const activeTab = this.itemsTarget.querySelector('#notification-tabs button[aria-selected="true"]')
    if (!activeTab) {
      console.error('No active tab found')
      return
    }

    const tabName = activeTab.dataset.tabName || 'all'
    const tabTarget = activeTab.dataset.tabsTarget
    if (!tabTarget) {
      console.error('No tab target found')
      return
    }

    const tabPanel = this.itemsTarget.querySelector(tabTarget)
    if (!tabPanel) {
      console.error('No tab panel found for:', tabTarget)
      return
    }

    const container = button.closest('.load-more-container')
    if (!container) return

    const loadBtn = container.querySelector('.load-more-btn')
    const spinner = container.querySelector('.loader-spinner')

    if (loadBtn) loadBtn.classList.add('hidden')
    if (spinner) spinner.classList.remove('hidden')

    try {
      const response = await get(`/homes/notifications?tab=${tabName}&page=${page}`, {
        responseKind: "html"
      })

      if (response.ok) {
        const html = await response.text

        container.remove()

        tabPanel.insertAdjacentHTML('beforeend', html)

        if (typeof initFlowbite !== 'undefined') {
          initFlowbite()
        }

        this.setupModalImageLazyLoading()
      }
    } catch (error) {
      console.error(`Error loading more notifications:`, error)
      if (loadBtn) loadBtn.classList.remove('hidden')
      if (spinner) spinner.classList.add('hidden')
    }
  }

  updateBadge() {
    const cookieKey = this.cookieKey();

    const lastVisited = document.cookie
      .split("; ")
      .find(c => c.startsWith(cookieKey))
      ?.split("=")[1];

    const lastVisitedDate = lastVisited ? new Date(new Date(lastVisited).getTime()) : null;

    const unreadCount = this.itemTargets.filter(el => {
      const detectedAt = new Date(el.dataset.detectedAt);
      return !lastVisitedDate || detectedAt > lastVisitedDate;
    }).length;

    this.showCount(unreadCount);
  }

  async updateBadgeOnLoad() {
    if (this.hasItemsTarget && this.itemsTarget.innerHTML.trim() !== "") {
      this.updateBadge()
      return
    }

    try {
      const response = await get('/homes/notifications?tab=all&page=1', {
        responseKind: "html"
      })

      if (response.ok) {
        const tempDiv = document.createElement('div')
        tempDiv.innerHTML = await response.text

        const newCountElement = tempDiv.querySelector('[data-new-notification-count]')
        if (newCountElement) {
          const newCount = parseInt(newCountElement.dataset.newNotificationCount) || 0
          this.showCount(newCount)
        }
      }
    } catch (error) {
      console.error('Error loading badge count:', error)
    }
  }

  showCount(count) {
    if (!this.hasBadgeTarget) return;

    if (count > 0) {
      this.badgeTarget.textContent = count;
      this.badgeTarget.classList.remove("hidden");
    } else {
      this.badgeTarget.classList.add("hidden");
    }
  }

  markVisited() {
    const latest = this.latestNotificationTime();
    if (!latest) return;

    const utc = latest.toISOString();

    if (this.hasUserIdValue) {
      document.cookie = `notifications_last_visited_at_${this.userIdValue}=${utc}; path=/; SameSite=Lax; max-age=31536000`;
    }

    localStorage.setItem("notifications_last_visited_at", utc);
    window.location.reload();
  }

  latestNotificationTime() {
    return this.itemTargets.reduce((latest, el) => {
      const d = new Date(el.dataset.detectedAt);
      return !latest || d > latest ? d : latest;
    }, null);
  }

  openDrawer() {
    document.body.classList.add("overflow-hidden");
    this.drawer.classList.remove("translate-x-full");
    this.overlay.classList.remove("hidden");
  }

  closeDrawer() {
    document.body.classList.remove("overflow-hidden");

    this.drawer.classList.add("translate-x-full");
    this.overlay.classList.add("hidden");

    this.markVisited();

    this.updateBadge();

    this.bellButton?.focus();
  }

  cookieKey() {
    return `notifications_last_visited_at_${this.userIdValue}`;
  }

  get drawer() {
    return this.element.querySelector("#drawer-navigation");
  }

  get overlay() {
    return this.element.querySelector("#drawer-overlay");
  }

  get bellButton() {
    return this.element.querySelector("[data-action*='notification#open']");
  }

  get frame() {
    return this.element.querySelector("turbo-frame#notifications");
  }
}
